'use client';

import { useEffect, useState } from 'react';

import { getSessionLabel, type Session } from '@/lib/auth/session';
import { readStoredSession, SESSION_CHANGE_EVENT, writeStoredSession } from '@/services/sessionStorage';

export interface SessionFlagState {
  /** False until sessionStorage has been read — keep chrome SSR-stable until then. */
  isReady: boolean;
  isAuthenticated: boolean;
  /** Username / email for chrome, or null when logged out. */
  accountLabel: string | null;
  signOut: () => void;
}

/**
 * Lightweight session presence for chrome (header). Reads sessionStorage only —
 * does not pull the login API into marketing page bundles.
 */
export function useSessionFlag(): SessionFlagState {
  const [isReady, setIsReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    const sync = () => {
      setSession(readStoredSession());
      setIsReady(true);
    };

    sync();
    window.addEventListener(SESSION_CHANGE_EVENT, sync);
    return () => window.removeEventListener(SESSION_CHANGE_EVENT, sync);
  }, []);

  return {
    isReady,
    isAuthenticated: Boolean(session?.token),
    accountLabel: getSessionLabel(session),
    signOut: () => writeStoredSession(null),
  };
}
